import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { InvoiceForm } from '@features/invoices/types/invoiceForm/invoiceForm'
import { getInvoicePayload } from '@features/invoices/utils/invoicePayload/getInvoicePayload'
import { useZervantSettings } from '@features/invoicingSettings/hooks/useZervantSettings'
import { ZervantTemplateSettings } from '@features/invoicingSettings/types/templates/zervantTemplateSettings'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useCurrentUser } from '@modules-deprecated/app/user'

import { useContactPersons } from '../../../../../hooks/useContactPersons'
import { InvoiceEditorUrlParams } from '../../../types/invoiceEditorUrlParams'
import { invoiceEditorVariantToBillingStage } from '../constants/variants/invoiceEditorVariantToBillingStage'
import { useInvoiceEditorDepositModal } from '../contexts/invoiceEditorDepositModalContext'
import { useInvoiceEditorForm } from '../contexts/invoiceEditorFormContext'
import { useInvoiceEditorBaseCurrency } from './useInvoiceEditorBaseCurrency'
import { useInvoiceEditorVariant } from './useInvoiceEditorVariant'
import { useInvoiceVisibleColumns } from './useInvoiceVisibleColumns'

export const useInvoiceDraftPayload = () => {
  const { t } = useTranslation()
  const { user, isLoading: isLoadingCurrentUser } = useCurrentUser()
  const { organization, isLoading: isOrganizationLoading } = useUserOrganization()
  const { data: zervantSettings, isLoading: isZervantSettingsLoading } = useZervantSettings()
  const { visibleColumns } = useInvoiceVisibleColumns()
  const { invoiceEditorForm } = useInvoiceEditorForm()
  const { baseCurrency, isLoading: isBaseCurrencyLoading } = useInvoiceEditorBaseCurrency()
  const { documentId } = useParams<InvoiceEditorUrlParams>()
  const { invoiceEditorVariant } = useInvoiceEditorVariant()
  const { depositConfig } = useInvoiceEditorDepositModal()

  const { getValues } = invoiceEditorForm

  const receiverId = getValues('receiver.id')

  const { contactPersons, isLoading: isLoadingContactPersons } = useContactPersons({ contactId: receiverId })

  const isLoading =
    isBaseCurrencyLoading ||
    isLoadingContactPersons ||
    isLoadingCurrentUser ||
    isOrganizationLoading ||
    isZervantSettingsLoading

  const getInvoiceDraftPayload = useCallback(
    (documentType: InvoiceDocumentType, formData?: InvoiceForm, customTemplateSettings?: ZervantTemplateSettings) => {
      const invoiceValues = formData || getValues?.()

      const primaryReceiverContactPerson = contactPersons?.find((contactPerson) => !!contactPerson.isPrimary)
      const creator = { id: user?.id, email: user?.email }

      return getInvoicePayload({
        approvedInvoiceId: documentId,
        baseCurrency,
        billingStage: invoiceEditorVariantToBillingStage[invoiceEditorVariant],
        creator,
        customTemplateSettings,
        depositConfig,
        documentType,
        invoiceValues,
        organization,
        receiverContactPerson: primaryReceiverContactPerson,
        t,
        visibleColumns,
        zervantSettings,
      })
    },
    [
      baseCurrency,
      contactPersons,
      depositConfig,
      documentId,
      getValues,
      invoiceEditorVariant,
      organization,
      t,
      user?.email,
      user?.id,
      visibleColumns,
      zervantSettings,
    ],
  )

  return {
    getInvoiceDraftPayload,
    isLoading,
  }
}
